import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Pagination, Table, Dropdown } from "flowbite-react";
import { API } from "aws-amplify";
import { IoMdArrowDropup, IoMdArrowDropdown } from "react-icons/io";
import { FaChevronDown } from "react-icons/fa";
import { createPortal } from "react-dom";
import Context from "../../../Context/Context";
import RevenueSection from "./RevenueCard";

import DateFormatter from "../../../common/utils/DateFormatter";
import PaginationComponent from "../../../common/Pagination";

// Custom Dropdown Component
const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        portalRef.current && !portalRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      // Always position below the button
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width,
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || null;
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-10 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="truncate">{displayValue}</span>
        <FaChevronDown 
          className={`ml-2 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={portalRef}
          className={`fixed z-[99999] rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="max-h-60 overflow-y-auto">
            {options.length === 0 ? (
              <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No options available
              </div>
            ) : (
              options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left transition-colors duration-150 ${
                    value === option.value
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                        : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                      : isDarkMode
                      ? 'text-white hover:bg-[#2a2a2a]'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

function PaymentDetails() {
  const [currentPage, setCurrentPage] = useState(1);
  const [cashoutAmount, setCashoutAmount] = useState(null);
  const [payments, setPayments] = useState([]);
  const { userData, isDarkMode = true } = useContext(Context);
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const months = useMemo(
    () => [
      "All time",
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  const [selectedMonth, setSelectedMonth] = useState(months[currentMonth]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  }, []);

  const SortIcon = React.memo(({ isActive, direction }) => (
    <span className="inline-flex flex-col ml-1 relative h-4 w-3">
      <IoMdArrowDropup
        className={`absolute top-0 ${
          isActive && direction === "ascending"
            ? "text-blue-600"
            : "text-gray-400"
        }`}
        size={12}
      />
      <IoMdArrowDropdown
        className={`absolute bottom-0 ${
          isActive && direction === "descending"
            ? "text-blue-600"
            : "text-gray-400"
        }`}
        size={12}
      />
    </span>
  ));

  useEffect(() => {
    const fetchCashoutAmount = async () => {
      if (!userData?.institution) return;

      try {
        const response = await API.get(
          "user",
          `/cashCollected/${userData.institution}`
        );
        if (response?.client?.length > 0) {
          setCashoutAmount({
            client: response.client,
            paymentDate: response.client[0]?.lastUpdated || null,
          });
        }
        setPayments(response.payments.items || []);
      } catch (error) {
        console.error("Error fetching cashout amount:", error);
        setCashoutAmount(null);
      }
    };

    fetchCashoutAmount();
  }, [userData?.institution, userData?.cognitoId]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (
      sortConfig.key === key &&
      sortConfig.direction === "descending"
    ) {
      key = null;
      direction = null;
    }
    setSortConfig({ key, direction });
  };

  const sortData = useMemo(() => {
    if (
      !sortConfig.key ||
      !sortConfig.direction ||
      !payments ||
      payments.length === 0
    )
      return payments;

    return [...payments].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case "userName":
          aValue = a.userDetails?.userName?.toLowerCase() || "";
          bValue = b.userDetails?.userName?.toLowerCase() || "";
          break;
        case "phoneNumber":
          aValue = a.userDetails?.phoneNumber || "";
          bValue = b.userDetails?.phoneNumber || "";
          break;
        case "products":
          aValue = a.userDetails?.products?.length || 0;
          bValue = b.userDetails?.products?.length || 0;
          break;
        case "amount":
          aValue = Number(a.amount) || 0;
          bValue = Number(b.amount) || 0;
          break;
        case "paymentDate":
          aValue = new Date(a.paymentDate || 0).getTime();
          bValue = new Date(b.paymentDate || 0).getTime();
          break;
        default:
          aValue = (
            a[sortConfig.key]?.toLowerCase?.() ||
            a[sortConfig.key] ||
            ""
          ).toString();
          bValue = (
            b[sortConfig.key]?.toLowerCase?.() ||
            b[sortConfig.key] ||
            ""
          ).toString();
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "ascending"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === "ascending"
        ? aValue - bValue
        : bValue - aValue;
    });
  }, [payments, sortConfig]);

  const filteredPayments = useMemo(() => {
    if (!payments || payments.length === 0) return [];

    let filtered = sortData || payments;
    if (selectedYear !== "All time") {
      filtered = sortData?.filter((payment) => {
        const date = new Date(payment.paymentDate);
        const paymentYear = date.getFullYear();
        const paymentMonth = date.getMonth() + 1;

        const isYearMatch = paymentYear === parseInt(selectedYear);
        const isMonthMatch =
          selectedMonth === "All time" ||
          paymentMonth === months.indexOf(selectedMonth);

        return isYearMatch && isMonthMatch;
      });
    }
    return filtered;
  }, [sortData, selectedYear, selectedMonth, months, payments]);
  console.log("Filtered Payments:", filteredPayments);

  const selectedPayments = useMemo(() => {
    return (
      filteredPayments?.slice((currentPage - 1) * 7, currentPage * 7) || []
    );

  }, [filteredPayments, currentPage]);
  

  const handleRowClick = (payment) => {
    console.log("Payment details:", payment);
  };

  const formatAmountWithCurrency = (amount, currency = "INR") => {
    const symbol = currency === "INR" ? "₹" : "$";
    const displayAmount =
      amount > 100 ? (amount / 100).toFixed(2) : amount.toFixed(2);
    return `${symbol} ${displayAmount}`;
  };

  const availableMonths = useMemo(() => {
    if (selectedYear === currentYear) {
      return months.slice(0, currentMonth + 1);
    }
    return months;
  }, [selectedYear, currentYear, currentMonth, months]);

  const yearOptions = useMemo(() => {
    return [
      { value: "All time", label: "All time" },
      ...years.map(year => ({ value: year, label: year.toString() }))
    ];
  }, [years]);

  const monthOptions = useMemo(() => {
    return availableMonths.map(month => ({ value: month, label: month }));
  }, [availableMonths]);

  const tableHeaders = [
    { key: "userName", label: "Name", className: "w-32" },
    { key: "phoneNumber", label: "Phone Number", className: "w-32" },
    { key: "paymentDate", label: "Payment Date", className: "w-32" },
    { key: "paymentMode", label: "Payment Mode", className: "w-32" },
    { key: "renew date", label: "renew date", className: "w-32" },
    { key: "amount", label: "Amount", className: "w-32" },
  ];

  const CustomTableHeadCell = React.memo(
    ({ onClick, children, sortKey, className, isDarkMode }) => (
      <Table.HeadCell
        className={`h-14 px-6 py-2 text-center text-xs font-semibold uppercase cursor-pointer ${
          isDarkMode ? 'text-gray-300 bg-[#0f0f0f]' : 'text-gray-700 bg-gray-50'
        } ${className}`}
        onClick={onClick}
      >
        <div className="flex items-center justify-center gap-1 h-full">
          <span className="truncate">{children}</span>
          <SortIcon
            isActive={sortConfig.key === sortKey}
            direction={sortConfig.direction}
          />
        </div>
      </Table.HeadCell>
    )
  );

  return (
    <div className={`w-full md:w-[calc(100%-5rem)] mx-auto mt-0 md:mt-10 flex flex-col h-full transition-colors duration-200 ${isDarkMode ? 'bg-black' : 'bg-white'}`} style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Header Section */}
      <div className={`w-full px-3 md:px-8 py-3 md:py-6 border-b ${isDarkMode ? 'border-[#2a2a2a]' : 'border-gray-200'}`}>
        {/* Title */}
        <div className="mb-4">
          <h1 className={`text-xl text-left md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>
            Revenue Management
          </h1>
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 items-start sm:items-center justify-start">
          {/* Year Filter */}
          <div className="w-full sm:w-40">
            <CustomDropdown
              value={selectedYear}
              onChange={setSelectedYear}
              options={yearOptions}
              placeholder="Select Year"
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Month Filter - Only show if not "All time" */}
          {selectedYear !== "All time" && (
            <div className="w-full sm:w-40">
              <CustomDropdown
                value={selectedMonth}
                onChange={setSelectedMonth}
                options={monthOptions}
                placeholder="Select Month"
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-3 md:px-8 py-3 md:py-6">
        {/* Revenue Cards */}
        <RevenueSection
          revenue={filteredPayments}
          cashoutAmount={cashoutAmount}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          isDarkMode={isDarkMode}
        />

        {/* Recent Transactions Section */}
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>
            Recent Transactions
          </h2>
          <button className={`text-xs md:text-sm font-semibold ${isDarkMode ? 'text-[#FDCF08] hover:text-[#f0c000]' : 'text-blue-600 hover:text-blue-700'}`}>
            View All
          </button>
        </div>
        <div className={`max-w-full mx-auto rounded-lg transition-colors duration-200 ${
          isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white border border-gray-200'
        }`}>
          <div className="overflow-x-auto">
            <Table hoverable className="min-w-full">
              <Table.Head className={isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}>
                <Table.HeadCell className={`px-4 md:px-6 py-3 text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-300 bg-[#0f0f0f]' : 'text-gray-700 bg-gray-50'}`}>
                  Member
                </Table.HeadCell>
                <Table.HeadCell className={`px-4 md:px-6 py-3 text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-300 bg-[#0f0f0f]' : 'text-gray-700 bg-gray-50'}`}>
                  Product / Service
                </Table.HeadCell>
                <Table.HeadCell className={`px-4 md:px-6 py-3 text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-300 bg-[#0f0f0f]' : 'text-gray-700 bg-gray-50'}`}>
                  Date
                </Table.HeadCell>
                <Table.HeadCell className={`px-4 md:px-6 py-3 text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-300 bg-[#0f0f0f]' : 'text-gray-700 bg-gray-50'}`}>
                  Status
                </Table.HeadCell>
                <Table.HeadCell className={`px-4 md:px-6 py-3 text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-300 bg-[#0f0f0f]' : 'text-gray-700 bg-gray-50'}`}>
                  Amount
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {selectedPayments?.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={5} className="text-center py-6">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No payment records found.
                      </p>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  selectedPayments?.map((payment) => (
                    <Table.Row
                      key={payment.paymentId}
                      className={`transition-colors border-none ${isDarkMode ? 'hover:bg-[#222222]' : 'hover:bg-gray-50'}`}
                      onClick={() => handleRowClick(payment)}
                    >
                      {/* Member with Avatar */}
                      <Table.Cell className={`px-4 md:px-6 py-4 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-white">
                              {(payment.emailId?.charAt(0) || "?").toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs md:text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {payment.emailId?.split("@")[0] || "Unknown"}
                            </div>
                            <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {payment.emailId || "N/A"}
                            </div>
                          </div>
                        </div>
                      </Table.Cell>

                      {/* Product/Service - Yellow Badge */}
                      <Table.Cell className={`px-4 md:px-6 py-4 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#FDCF08]/20 text-[#FDCF08]">
                          {payment.paymentMode === "offline" ? "Offline" : "Online"}
                        </span>
                      </Table.Cell>

                      {/* Date */}
                      <Table.Cell className={`px-4 md:px-6 py-4 text-xs md:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <DateFormatter epochDate={payment.paymentDate} />
                      </Table.Cell>

                      {/* Status - Green Badge */}
                      <Table.Cell className="px-4 md:px-6 py-4">
                        <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
                          Paid
                        </span>
                      </Table.Cell>

                      {/* Amount */}
                      <Table.Cell className={`px-4 md:px-6 py-4 text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatAmountWithCurrency(
                          payment.amount,
                          payment.currency
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </div>

          {/* Pagination */}
          <div className={`px-3 md:px-6 py-3 md:py-4 border-t ${isDarkMode ? 'border-[#2a2a2a] bg-[#0f0f0f]' : 'border-gray-100 bg-gray-50'}`}>
            <PaginationComponent
              showIcons={true}
              data={filteredPayments}
              itemsPerPage={7}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentDetails;
